/**
 * memberAuthenticatedFetch — the dao-suite member auth transport.
 *
 * Story: uid-001-dao-suite-jwt-auth-migration (unblocks frontend-001-9f-c);
 * silent-refresh added by uid-001-suite-login-frontdoor.
 *
 * A thin, BACKWARD-COMPATIBLE wrapper over the shared
 * `@hello-world-dao/auth` `authenticatedFetch`. Every dao-suite backend client
 * (events / blog / governance / members / member-profile / notification-prefs /
 * search) calls THIS instead of the raw shared fetch, so the JWT migration lands
 * in ONE place rather than every call site.
 *
 * **What it adds:** when an identity-service ES256 JWT is present in the in-memory
 * store, it injects `Authorization: Bearer <jwt>`. oracle-bridge (uid-001 JWT
 * middleware) verifies that JWT against the identity-service JWKS and prefers it;
 * if the JWT is absent the request still carries the session cookie
 * (`credentials: 'include'`, added by the underlying shared fetch) and oracle-
 * bridge falls back to cookie auth — exactly today's behaviour.
 *
 * **Why send both during transition:** the suite can present the JWT AND the
 * cookie simultaneously. oracle-bridge tries the JWT first and the cookie second,
 * so a half-migrated environment (JWT path enabled on some routes, JWKS flag off
 * on others) never breaks — there is no hard cutover. Once the cookie path is
 * retired (a later story), this wrapper is the single place the cookie carriage
 * is dropped.
 *
 * The shared `authenticatedFetch` keeps owning COOKIE 401-refresh, retry/backoff,
 * and session-expiry redirect. This wrapper decorates the request headers AND owns
 * the identity-service JWT silent-refresh: when a JWT-bearing call comes back 401
 * (the ES256 access JWT expired), it transparently refreshes via the httpOnly
 * `hwdao_refresh` cookie (`POST /token/refresh`), stores the fresh JWT, and retries
 * the original request ONCE. On refresh failure it drops the dead JWT and retries
 * cookie-only — never hard-bouncing a still-valid cookie session
 * (uid-001-suite-login-frontdoor).
 *
 * **bl-789 — the JWT path NEVER routes through the shared 401-interceptor.**
 * The shared `authenticatedFetch` reacts to ANY 401 by firing its OWN
 * `POST /token/refresh` and then a session-expiry redirect to the login front-door.
 * When a member hits an oracle-bridge member-data route that returns a NON-EXPIRY
 * 401 (e.g. "no matching oracle-bridge user for ic_principal", an AUTHORIZATION
 * failure — not an expired access JWT), handing that 401 to the shared interceptor
 * produces a SECOND, uncoordinated refresh racing this wrapper's own single-flight
 * refresh. The identity-service rotates the refresh token per call and REVOKES THE
 * WHOLE FAMILY on reuse → forced logout + dead `hwdao_refresh` cookie that wedges
 * re-login. So the JWT path issues its requests via a plain credentialed `fetch`
 * and OWNS the entire 401 lifecycle here: at most ONE single-flight refresh, and a
 * 401 that persists after that refresh is SURFACED to the caller as an error — never
 * a second refresh, never a family-revoke, never a front-door bounce. A member-data
 * authorization failure must not be able to destroy the session. The cookie-only
 * path (no JWT stored) is unchanged: it still delegates to the shared layer.
 *
 * **authz-001-9 — bounded transient retry restored on the plain-fetch path.**
 * Routing the member-data path around the shared `authenticatedFetch` (bl-789) also
 * dropped the shared layer's transient-retry/backoff. `memberFetch` now runs through
 * a REFRESH-FREE `fetchWithTransientRetry` that retries network-level failures (for
 * any method — the request never reached the origin) and 502/503/504 (idempotent
 * methods only — a non-idempotent write must not be blindly re-sent without an
 * idempotency key). Retry and 401-refresh stay orthogonal; the 401 lifecycle above
 * is untouched. The helper is duplicated byte-for-byte in otter-camp-suite.
 */

import { authenticatedFetch } from '@hello-world-dao/auth';
import { getIdentityJwt, setIdentityJwt, clearIdentityJwt } from './identity-jwt-store';
import { refreshIdentityJwt } from './identity-service-client';

/**
 * Merge an `Authorization: Bearer` header into a RequestInit's headers without
 * dropping any caller-supplied headers, accepting the three Headers shapes the
 * Fetch API allows (`Headers`, `string[][]`, and `Record<string,string>`).
 */
function withBearer(options: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(options?.headers);
  // Do not clobber an explicit Authorization header a caller already set.
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...options, headers };
}

/**
 * Single-flight refresh. The identity-service refresh token ROTATES on every
 * `/token/refresh` and the backend REVOKES THE WHOLE TOKEN FAMILY on reuse
 * (replay detection). A page that fans out N backend calls produces N concurrent
 * 401s when the access JWT expires; if each fired its own refresh they would all
 * present the SAME (not-yet-rotated) cookie → the 2nd..Nth look like a replay →
 * forced logout. Coalescing all concurrent refreshes onto ONE in-flight promise
 * guarantees the rotating cookie is presented exactly once.
 */
let inFlightRefresh: Promise<string | null> | null = null;
function refreshOnce(): Promise<string | null> {
  if (!inFlightRefresh) {
    inFlightRefresh = refreshIdentityJwt().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

// ---------------------------------------------------------------------------
// Bounded transient-retry for the member-data (plain-fetch) path — authz-001-9.
//
// DUPLICATED, BYTE-FOR-BYTE, in otter-camp-suite/src/services/member-authenticated-fetch.ts.
// A change here MUST be mirrored there (and vice-versa). Per the authz-001-9
// orchestrator disposition the retry helper is intentionally duplicated per-suite
// rather than extracted into `@hello-world-dao/auth`: a shared-package change
// means a publish + consumer-wave bump across 5+ suites (the bl-789 high-blast-
// radius history), whereas two small identical helpers are cheaper. Extraction is
// a clean follow-up if a third consumer appears.
//
// This restores the transient-retry the member-data JWT path LOST when it was
// routed around the shared `authenticatedFetch`'s `fetchWithRetry` to fix bl-789
// (the shared 401-interceptor must never see a member-data request). Retry and
// 401-refresh are orthogonal: this helper never inspects a 401 and never refreshes.
//
// Semantics (mirror the shared `authServiceClient` `fetchWithRetry` shape):
//  - Network-level failures (`fetch` throws — connection refused, DNS, abort) are
//    retried for EVERY method: the request never reached the origin, so a retry
//    cannot double-apply a non-idempotent write.
//  - 502/503/504 gateway statuses are retried ONLY for idempotent methods
//    (GET/HEAD). For POST/PUT/PATCH/DELETE a 5xx is NOT retried: with no
//    idempotency-key mechanism in oracle-bridge/blog-service, the origin may have
//    already processed the write and a blind retry could double-submit.
//  - Bounded: max 3 retries (4 attempts total), exponential backoff 1000·2^attempt.
//  - AbortError (explicit cancellation, or an upstream caller-owned timeout, e.g.
//    governanceCanister.ts's PROPOSAL_LIST_TIMEOUT_MS) is NEVER retried — the
//    caller has already given up on this request. Retrying would burn the full
//    ~7s backoff budget against an already-aborted signal before re-throwing the
//    same AbortError, silently multiplying the caller's own timeout/cancellation
//    latency instead of respecting it.
// ---------------------------------------------------------------------------

const MEMBER_FETCH_MAX_RETRIES = 3;
const MEMBER_FETCH_BASE_DELAY_MS = 1000;
const MEMBER_FETCH_RETRYABLE_STATUS = new Set([502, 503, 504]);

/** Idempotent methods may safely retry a transient 5xx; writes may not. */
function isIdempotentMethod(method: string | undefined): boolean {
  const m = (method ?? 'GET').toUpperCase();
  return m === 'GET' || m === 'HEAD';
}

/** Replaceable sleep seam so tests can make the backoff instant. @internal */
let _retrySleep: (ms: number) => Promise<void> = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Override the retry-backoff sleep. For testing only. Pass `undefined` to restore
 * the default `setTimeout`-based sleep.
 * @internal
 */
export function _setRetrySleepFn(fn: ((ms: number) => Promise<void>) | undefined): void {
  _retrySleep = fn ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
}

/**
 * Fetch with bounded, transient-only retry. See the block comment above for the
 * exact retry policy (network-errors-any-method; 5xx idempotent-only; max 3
 * retries; exponential backoff).
 */
async function fetchWithTransientRetry(url: string, options: RequestInit): Promise<Response> {
  const idempotent = isIdempotentMethod(options.method);
  let lastError: unknown;

  for (let attempt = 0; attempt <= MEMBER_FETCH_MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url, options);
      // Idempotent + transient gateway status → retry (bounded). A non-idempotent
      // write NEVER retries a 5xx (no idempotency key ⇒ double-submit risk).
      if (
        idempotent &&
        MEMBER_FETCH_RETRYABLE_STATUS.has(response.status) &&
        attempt < MEMBER_FETCH_MAX_RETRIES
      ) {
        await _retrySleep(MEMBER_FETCH_BASE_DELAY_MS * 2 ** attempt);
        continue;
      }
      return response;
    } catch (error) {
      // AbortError (explicit cancellation or an upstream caller-owned timeout)
      // must never be retried — the caller has already given up on this request.
      // DOMException (thrown by a real aborted fetch) may not be `instanceof
      // Error` in every environment, so check `.name` directly rather than
      // guarding on `instanceof Error` alone (same discipline as
      // governanceCanister.ts's abort handling).
      const errorName = error instanceof Error ? error.name : (error as { name?: string })?.name;
      if (errorName === 'AbortError') {
        throw error;
      }
      // Network-level failure: the request never reached the origin, so retrying
      // is safe for every method (including non-idempotent writes).
      lastError = error;
      if (attempt < MEMBER_FETCH_MAX_RETRIES) {
        await _retrySleep(MEMBER_FETCH_BASE_DELAY_MS * 2 ** attempt);
        continue;
      }
      throw error;
    }
  }

  // Unreachable in practice: the loop always returns a response or throws above.
  // Present so the type-checker sees a terminal statement.
  throw lastError ?? new Error('member fetch failed after retries');
}

/**
 * Plain credentialed fetch for the JWT (member-data) path, with bounded transient
 * retry (authz-001-9).
 *
 * Mirrors the shared `authenticatedFetch`'s `credentials: 'include'` so the session
 * cookie still rides along, but DELIBERATELY does not run the shared layer's 401
 * interceptor (its own `/token/refresh` + session-expiry redirect). The wrapper owns
 * the 401 lifecycle for member-data routes; see the bl-789 note in the file header.
 * The transient retry restored here is refresh-free and orthogonal to that 401
 * lifecycle (authz-001-9 AC4).
 */
function memberFetch(url: string, options: RequestInit): Promise<Response> {
  return fetchWithTransientRetry(url, { ...options, credentials: 'include' });
}

/**
 * Session-aware fetch for dao-suite member backend calls.
 *
 * When no JWT is stored, delegates to the shared `authenticatedFetch` (cookie-only,
 * byte-for-byte the previous behaviour: it adds `credentials: 'include'` and owns
 * COOKIE refresh/retry/redirect).
 *
 * When a JWT IS stored, the request is decorated with `Authorization: Bearer <jwt>`
 * and issued via a plain credentialed `fetch` — NOT the shared 401-interceptor (see
 * the bl-789 note in the file header). The wrapper owns the 401 lifecycle:
 *  - On a 401 it makes AT MOST ONE single-flight refresh off the httpOnly cookie,
 *    stores the fresh JWT, and retries the original request once.
 *  - A 401 that PERSISTS after that single refresh is SURFACED to the caller as an
 *    error — never a second refresh, never a refresh-token-family revoke, never a
 *    front-door bounce. (A member-data authorization 401 must not destroy the
 *    session.)
 *  - On refresh FAILURE it drops the dead JWT and retries once cookie-only via the
 *    plain credentialed fetch, surfacing whatever that returns — still no front-door
 *    bounce from this wrapper.
 *
 * @param url - the request URL
 * @param options - standard RequestInit (Authorization is added unless already present)
 * @returns the fetch Response
 */
export async function memberAuthenticatedFetch(
  url: string,
  options?: RequestInit,
): Promise<Response> {
  const token = getIdentityJwt();

  // No JWT stored → cookie-only path, unchanged (delegates to the shared layer).
  if (!token) {
    return authenticatedFetch(url, options ?? {});
  }

  const response = await memberFetch(url, withBearer(options, token));

  // A 200..399/4xx-other response, or a 401 from a request the caller already
  // authorized, needs no JWT refresh — return as-is.
  if (response.status !== 401) {
    return response;
  }

  // JWT-bearing call came back 401. Make AT MOST ONE single-flight refresh off the
  // httpOnly cookie. The shared 401-interceptor is intentionally NOT involved — a
  // second uncoordinated refresh would race this one and revoke the token family.
  const freshJwt = await refreshOnce();
  if (!freshJwt) {
    // Refresh failed (expired / revoked / reused / no cookie). Drop the dead JWT and
    // retry ONCE cookie-only via the plain credentialed fetch: a still-valid cookie
    // session keeps working, and a truly-dead session simply surfaces its 401 to the
    // caller — never a hard front-door bounce from this wrapper.
    clearIdentityJwt();
    return memberFetch(url, options ?? {});
  }

  setIdentityJwt(freshJwt);
  // Retry exactly once with the fresh bearer. Whatever this returns — including a
  // PERSISTENT non-expiry 401 (e.g. "no matching oracle-bridge user") — is surfaced
  // to the caller. No second refresh, no family revoke, no logout.
  return memberFetch(url, withBearer(options, freshJwt));
}
